import { toJS } from "mobx";

import { useFileContent } from "@/components/ui/FileDisplay/hooks";
import { Variable } from "@/components/InputVariableConfigurator/types";

interface UseFileOperationsProps {
  variable: Variable;
  onChange: (variable: Variable) => void;
}

export const useFileOperations = ({ variable, onChange }: UseFileOperationsProps) => {
  const { fileItems, isFileContent, isProcessingFile } = useFileContent(
    typeof variable.value === "object"
      ? JSON.stringify(toJS(variable.value))
      : String(variable.value),
  );

  const handleDeleteFile = (index: number) => {
    try {
      const parsedValue = JSON.parse(variable.value as string);

      if (index === 0 && typeof parsedValue === "string") {
        onChange({ ...variable, value: "" });
        return;
      }

      if (Array.isArray(parsedValue)) {
        const updatedFiles = parsedValue.filter((_, i) => i !== index);

        const newValue =
          updatedFiles.length === 1
            ? JSON.stringify(updatedFiles[0])
            : JSON.stringify(updatedFiles);

        onChange({ ...variable, value: newValue });
      }
    } catch {
      onChange({ ...variable, value: "" });
    }
  };

  const handleAddFile = (fileUrl: string) => {
    onChange({
      ...variable,
      value: JSON.stringify([...(fileItems || []).map(item => item.data), fileUrl]),
    });
  };

  const handleAddFiles = (fileUrls: string[]) => {
    onChange({
      ...variable,
      value: JSON.stringify([...(fileItems || []).map(item => item.data), ...fileUrls]),
    });
  };

  const handleSaveFile = (newData: string, previousData: string) => {
    const updatedFiles = fileItems?.filter(file => file.data !== previousData);
    onChange({
      ...variable,
      value: JSON.stringify([...(updatedFiles || []).map(item => item.data), newData]),
    });
  };

  return {
    fileItems,
    isFileContent,
    isProcessingFile,
    handleDeleteFile,
    handleAddFile,
    handleAddFiles,
    handleSaveFile,
  };
};
